import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Select,
} from '@mui/material';
import {
  PersonOutline as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CameraAlt as CameraIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Public as PublicIcon,
  Instagram as InstagramIcon,
  HourglassEmpty as HourglassIcon,
  RestartAlt as RestartIcon,
} from '@mui/icons-material';
import { countries } from '../../data/countries';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';
import { registerDriverAsync } from '../../redux/driverSlice';

const steps = ['Personal', 'Contact', 'Sport Info', 'History'];

const CountrySelect = ({ label, name, value, onChange, icon: Icon = PublicIcon, countries }) => {
  const [search, setSearch] = useState('');

  return (
    <TextField
      fullWidth
      select
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      variant="filled"
      sx={inputStyles}
      SelectProps={{
        renderValue: (selected) => {
          const country = countries.find(c => c.name === selected);
          if (!selected) return <span style={{ color: '#94a3b8' }}>Select {label}</span>;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {country && <span style={{ fontSize: '1.2rem' }}>{country.flag}</span>}
              <span>{selected}</span>
            </Box>
          );
        },
        MenuProps: {
          PaperProps: {
            'data-lenis-prevent': '',
            sx: {
              maxHeight: 300,
              bgcolor: '#111827',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'rgba(251, 191, 36, 0.3)',
                borderRadius: '4px',
                '&:hover': { bgcolor: 'rgba(251, 191, 36, 0.5)' },
              },
              '& .MuiMenuItem-root': {
                fontSize: '0.95rem',
                py: 1.5,
                '&:hover': { bgcolor: 'rgba(251, 191, 36, 0.1)' },
                '&.Mui-selected': { bgcolor: 'rgba(251, 191, 36, 0.2)' },
                '&.Mui-selected:hover': { bgcolor: 'rgba(251, 191, 36, 0.3)' },
              }
            }
          }
        }
      }}
      InputProps={{
        disableUnderline: true,
        startAdornment: (
          <InputAdornment position="start">
            <Icon className="input-icon" sx={{ fontSize: '1.2rem', transition: 'color 0.3s ease' }} />
          </InputAdornment>
        ),
      }}
    >
      <MenuItem
        disableRipple
        onKeyDown={(e) => e.stopPropagation()}
        sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#111827 !important', pt: 1, pb: 1 }}
      >
        <input
          placeholder="Search country..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          style={{
            width: '100%',
            padding: '10px 14px',
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '0.9rem',
            outline: 'none',
            fontFamily: 'inherit',
          }}
          onFocus={(e) => e.target.style.borderColor = '#fbbf24'}
          onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </MenuItem>
      {countries.filter(c => c.name.toLowerCase().includes(search.toLowerCase())).map((country) => (
        <MenuItem key={country.iso2} value={country.name}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '1.2rem' }}>{country.flag}</span>
            <span>{country.name}</span>
            <span style={{ color: '#94a3b8', fontSize: '0.8rem', marginLeft: '4px' }}>({country.iso3})</span>
          </Box>
        </MenuItem>
      ))}
    </TextField>
  );
};

const RegistrationForm = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.driver);
  const [activeStep, setActiveStep] = useState(0);
  const [showHistoryText, setShowHistoryText] = useState(false);
  const [countryCodeSearch, setCountryCodeSearch] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    dob: '',
    gender: '',
    nationality: '',
    countryCode: '+91',
    phone: '',
    email: '',
    socialId: '',
    asnName: '',
    asnCountry: '',
    interestedInAAGC: '',
    hasHistory: '',
    historyDescription: '',
    profilePhoto: null,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const handlePhotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, profilePhoto: file }));
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    }
  };

  const handleRemovePhoto = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, profilePhoto: null }));
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoPreview(null);
    const fileInput = document.getElementById('photo-upload');
    if (fileInput) fileInput.value = '';
  };

  const handleRegisterAnother = () => {
    setIsSubmitted(false);
    setPhotoPreview(null);
    setActiveStep(0);
  };

  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'hasHistory') {
      setShowHistoryText(value === 'yes');
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.fullName.trim() || !formData.dob || !formData.nationality || !formData.gender) {
        setSnackbar({
          open: true,
          message: 'Please complete all required fields in Step 1 before proceeding.',
          severity: 'warning',
        });
        return;
      }
    } else if (activeStep === 1) {
      if (!formData.phone.trim() || !formData.email.trim()) {
        setSnackbar({
          open: true,
          message: 'Please enter your phone number and email address before proceeding.',
          severity: 'warning',
        });
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    // Validation checks
    if (!formData.fullName.trim() || !formData.dob || !formData.nationality || !formData.gender) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required personal details (Step 1).',
        severity: 'warning',
      });
      setActiveStep(0);
      return;
    }
    if (!formData.phone.trim() || !formData.email.trim()) {
      setSnackbar({
        open: true,
        message: 'Please fill in your phone number and email address (Step 2).',
        severity: 'warning',
      });
      setActiveStep(1);
      return;
    }

    const data = new FormData();
    data.append('fullName', formData.fullName.trim());
    data.append('dateOfBirth', formData.dob);
    data.append('nationality', formData.nationality);
    data.append(
      'gender',
      formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1).toLowerCase()
    );
    data.append('phoneNumber', `${formData.countryCode} ${formData.phone}`.trim());
    data.append('email', formData.email.trim());

    if (formData.socialId.trim()) {
      data.append('socialMedia', formData.socialId.trim());
    }
    if (formData.asnName.trim()) {
      data.append('asnName', formData.asnName.trim());
    }
    if (formData.asnCountry) {
      data.append('asnCountry', formData.asnCountry);
    }

    data.append('interestedInAagc', formData.interestedInAAGC === 'yes' ? 'true' : 'false');
    data.append('hasMotorsportHistory', formData.hasHistory === 'yes' ? 'true' : 'false');

    if (formData.profilePhoto) {
      data.append('profilePhoto', formData.profilePhoto);
    }

    const resultAction = await dispatch(registerDriverAsync(data));

    if (registerDriverAsync.fulfilled.match(resultAction)) {
      setSubmittedData({ ...formData, photoPreview });
      setIsSubmitted(true);
      setSnackbar({
        open: true,
        message: resultAction.payload?.message || 'Driver registration submitted successfully!',
        severity: 'success',
      });
      setActiveStep(0);
      setFormData({
        fullName: '',
        dob: '',
        gender: '',
        nationality: '',
        countryCode: '+91',
        phone: '',
        email: '',
        socialId: '',
        asnName: '',
        asnCountry: '',
        interestedInAAGC: 'no',
        hasHistory: 'no',
        historyDescription: '',
        profilePhoto: null,
      });
      setPhotoPreview(null);
    } else {
      setSnackbar({
        open: true,
        message: resultAction.payload || 'Failed to submit driver registration.',
        severity: 'error',
      });
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            key="step1"
          >
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#fbbf24',
                  mb: 1,
                  fontFamily: 'Bebas Neue',
                  letterSpacing: '1.5px',
                  fontSize: '1.5rem'
                }}
              >
                Step 1: Personal Details
              </Typography>
              <Box sx={{ width: '60px', height: '3px', bgcolor: '#fbbf24', opacity: 0.6, borderRadius: '2px' }} />
            </Box>

            <Grid container spacing={4} alignItems="flex-start">
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  variant="filled"
                  sx={inputStyles}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon className="input-icon" sx={{ fontSize: '1.2rem', transition: 'color 0.3s ease' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={formData.dob}
                  onChange={handleChange}
                  variant="filled"
                  InputLabelProps={{ shrink: true }}
                  sx={inputStyles}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <CountrySelect
                  label="Nationality"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleChange}
                  countries={countries}
                  icon={PublicIcon}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <FormControl component="fieldset" sx={{ width: '100%', mt: -0.5 }}>
                  <FormLabel sx={{ color: '#94a3b8', mb: 0.5, fontSize: '0.85rem', fontWeight: 500, '&.Mui-focused': { color: '#fbbf24' } }}>
                    Gender
                  </FormLabel>
                  <RadioGroup row name="gender" value={formData.gender} onChange={handleChange} sx={{ minHeight: '56px', alignItems: 'center' }}>
                    <FormControlLabel value="male" control={<Radio sx={radioStyles} size="small" />} label="Male" sx={{ color: '#ffffff', '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }} />
                    <FormControlLabel value="female" control={<Radio sx={radioStyles} size="small" />} label="Female" sx={{ color: '#ffffff', '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }} />
                    <FormControlLabel value="other" control={<Radio sx={radioStyles} size="small" />} label="Other" sx={{ color: '#ffffff', '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }} />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </motion.div>
        );
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            key="step2"
          >
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#fbbf24',
                  mb: 1,
                  fontFamily: 'Bebas Neue',
                  letterSpacing: '1.5px',
                  fontSize: '1.5rem'
                }}
              >
                Step 2: Contact Information
              </Typography>
              <Box sx={{ width: '60px', height: '3px', bgcolor: '#fbbf24', opacity: 0.6, borderRadius: '2px' }} />
            </Box>

            <Grid container spacing={4} alignItems="flex-start">
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="filled"
                  sx={inputStyles}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon className="input-icon" sx={{ fontSize: '1.2rem', transition: 'color 0.3s ease', mr: 1 }} />
                        <Select
                          variant="standard"
                          disableUnderline
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={handleChange}
                          MenuProps={{
                            PaperProps: {
                              'data-lenis-prevent': '',
                              sx: {
                                maxHeight: 300,
                                bgcolor: '#111827',
                                color: '#ffffff',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': { width: '8px' },
                                '&::-webkit-scrollbar-track': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                                '&::-webkit-scrollbar-thumb': {
                                  bgcolor: 'rgba(251, 191, 36, 0.3)',
                                  borderRadius: '4px',
                                  '&:hover': { bgcolor: 'rgba(251, 191, 36, 0.5)' },
                                },
                                '& .MuiMenuItem-root': {
                                  fontSize: '0.95rem',
                                  py: 1.5,
                                  '&:hover': { bgcolor: 'rgba(251, 191, 36, 0.1)' },
                                  '&.Mui-selected': { bgcolor: 'rgba(251, 191, 36, 0.2)' },
                                  '&.Mui-selected:hover': { bgcolor: 'rgba(251, 191, 36, 0.3)' },
                                }
                              }
                            }
                          }}
                          sx={{
                            color: '#e2e8f0', // Soft white text
                            '& .MuiSelect-select': { py: 0, pl: 0, pr: 1 },
                            mr: 1,
                            fontSize: '0.95rem',
                            '&::before, &::after': { display: 'none' },
                            '& .MuiSvgIcon-root': { color: '#94a3b8' } // Icon matches the phone icon
                          }}
                        >
                          <MenuItem
                            disableRipple
                            onKeyDown={(e) => e.stopPropagation()}
                            sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#111827 !important', pt: 1, pb: 1 }}
                          >
                            <input
                              placeholder="Search code..."
                              value={countryCodeSearch}
                              onChange={(e) => setCountryCodeSearch(e.target.value)}
                              autoFocus
                              style={{
                                width: '100%',
                                padding: '10px 14px',
                                background: 'rgba(255,255,255,0.07)',
                                border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: '8px',
                                color: '#fff',
                                fontSize: '0.9rem',
                                outline: 'none',
                                fontFamily: 'inherit',
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#fbbf24'}
                              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </MenuItem>
                          {countries.filter(c => c.phone && (c.iso2.toLowerCase().includes(countryCodeSearch.toLowerCase()) || c.phone.includes(countryCodeSearch) || c.name.toLowerCase().includes(countryCodeSearch.toLowerCase()))).map(c => (
                            <MenuItem key={c.iso2} value={c.phone}>
                              {c.iso2} {c.phone}
                            </MenuItem>
                          ))}
                        </Select>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="filled"
                  sx={inputStyles}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon className="input-icon" sx={{ fontSize: '1.2rem', transition: 'color 0.3s ease' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Instagram/Facebook ID"
                  name="socialId"
                  value={formData.socialId}
                  onChange={handleChange}
                  variant="filled"
                  sx={inputStyles}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <InstagramIcon className="input-icon" sx={{ fontSize: '1.2rem', transition: 'color 0.3s ease' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </motion.div>
        );
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            key="step3"
          >
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#fbbf24',
                  mb: 1,
                  fontFamily: 'Bebas Neue',
                  letterSpacing: '1.5px',
                  fontSize: '1.5rem'
                }}
              >
                Step 3: Sport & Participation
              </Typography>
              <Box sx={{ width: '60px', height: '3px', bgcolor: '#fbbf24', opacity: 0.6, borderRadius: '2px' }} />
            </Box>

            <Grid container spacing={4} alignItems="flex-start">
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="ASN Name"
                  name="asnName"
                  value={formData.asnName}
                  onChange={handleChange}
                  variant="filled"
                  sx={inputStyles}
                  InputProps={{ disableUnderline: true }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <CountrySelect
                  label="ASN Country"
                  name="asnCountry"
                  value={formData.asnCountry}
                  onChange={handleChange}
                  countries={countries}
                  icon={PublicIcon}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <FormControl component="fieldset" sx={{ width: '100%', mt: -0.5 }}>
                  <FormLabel sx={{ color: '#94a3b8', mb: 0.5, fontSize: '0.85rem', fontWeight: 500, '&.Mui-focused': { color: '#fbbf24' } }}>
                    Are you interested in participating in the AAGC event?
                  </FormLabel>
                  <RadioGroup row name="interestedInAAGC" value={formData.interestedInAAGC} onChange={handleChange} sx={{ minHeight: '56px', alignItems: 'center' }}>
                    <FormControlLabel value="yes" control={<Radio sx={radioStyles} size="small" />} label="YES" sx={{ color: '#ffffff', '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }} />
                    <FormControlLabel value="no" control={<Radio sx={radioStyles} size="small" />} label="NO" sx={{ color: '#ffffff', '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }} />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </motion.div>
        );
      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            key="step4"
          >
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#fbbf24',
                  mb: 1,
                  fontFamily: 'Bebas Neue',
                  letterSpacing: '1.5px',
                  fontSize: '1.5rem'
                }}
              >
                Step 4: History & Final Polish
              </Typography>
              <Box sx={{ width: '60px', height: '3px', bgcolor: '#fbbf24', opacity: 0.6, borderRadius: '2px' }} />
            </Box>

            <Grid container spacing={4} alignItems="flex-start">
              <Grid item size={{ xs: 12 }} marginBottom={-3}>
                <FormControl component="fieldset" sx={{ width: '100%', mt: -0.5 }}>
                  <FormLabel sx={{ color: '#94a3b8', mb: 0.5, fontSize: '0.85rem', fontWeight: 500, '&.Mui-focused': { color: '#fbbf24' } }}>
                    MOTOR SPORTS PAST HISTORY
                  </FormLabel>
                  <RadioGroup row name="hasHistory" value={formData.hasHistory} onChange={handleChange} sx={{ minHeight: '56px', alignItems: 'center' }}>
                    <FormControlLabel value="yes" control={<Radio sx={radioStyles} size="small" />} label="YES" sx={{ color: '#ffffff', '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }} />
                    <FormControlLabel value="no" control={<Radio sx={radioStyles} size="small" />} label="NO" sx={{ color: '#ffffff', '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }} />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <AnimatePresence>
                {showHistoryText && (
                  <Grid item size={{ xs: 12 }} component={motion.div} initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Please share past history"
                      name="historyDescription"
                      value={formData.historyDescription}
                      onChange={handleChange}
                      variant="filled"
                      sx={inputStyles}
                      InputProps={{ disableUnderline: true }}
                    />
                  </Grid>
                )}

                <Grid item size={{ xs: 12, sm: 6 }}>
                  <input
                    type="file"
                    id="photo-upload"
                    accept="image/*"
                    hidden
                    onChange={handlePhotoChange}
                  />
                  {photoPreview ? (
                    <Box
                      sx={{
                        border: '2px solid rgba(251, 191, 36, 0.5)',
                        borderRadius: '16px',
                        p: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Box
                        component="img"
                        src={photoPreview}
                        alt="Profile Preview"
                        sx={{
                          width: 68,
                          height: 68,
                          borderRadius: '12px',
                          objectFit: 'cover',
                          border: '2px solid #fbbf24',
                          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.5)',
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {formData.profilePhoto?.name || 'Selected Photo'}
                        </Typography>
                        <Typography sx={{ color: '#10b981', fontSize: '0.78rem', fontWeight: 600, mt: 0.2 }}>
                          ✓ Photo attached
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            onClick={() => document.getElementById('photo-upload').click()}
                            sx={{
                              minWidth: 'auto',
                              py: 0.3,
                              px: 1.2,
                              fontSize: '0.75rem',
                              color: '#fbbf24',
                              border: '1px solid rgba(251, 191, 36, 0.4)',
                              borderRadius: '6px',
                              textTransform: 'none',
                              '&:hover': { bgcolor: 'rgba(251, 191, 36, 0.1)' },
                            }}
                          >
                            Change
                          </Button>
                          <Button
                            size="small"
                            onClick={handleRemovePhoto}
                            sx={{
                              minWidth: 'auto',
                              py: 0.3,
                              px: 1.2,
                              fontSize: '0.75rem',
                              color: '#ef4444',
                              border: '1px solid rgba(239, 68, 68, 0.4)',
                              borderRadius: '6px',
                              textTransform: 'none',
                              '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' },
                            }}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: '2px dashed rgba(251, 191, 36, 0.35)',
                        borderRadius: '16px',
                        p: 2.5,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        bgcolor: 'rgba(255, 255, 255, 0.03)',
                        '&:hover': {
                          borderColor: '#fbbf24',
                          bgcolor: 'rgba(251, 191, 36, 0.08)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                      onClick={() => document.getElementById('photo-upload').click()}
                    >
                      <CameraIcon sx={{ fontSize: '2.2rem', color: '#fbbf24', mb: 0.5 }} />
                      <Typography sx={{ color: '#ffffff', fontSize: '0.95rem', fontWeight: 600 }}>
                        Upload Driver Profile Photo
                      </Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', mt: 0.3 }}>
                        PNG, JPG or WEBP (Max 5MB)
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </AnimatePresence>
            </Grid>
          </motion.div>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        bgcolor: '#0a0a0a',
        py: { xs: 10, md: 15 },
        position: 'relative',
        overflow: 'hidden',
        background: 'radial-gradient(circle at 80% 70%, #111827 0%, #0a0a0a 100%)',
      }}
    >
      {/* Background Decoration Text */}
      <Typography
        variant="h1"
        sx={{
          zIndex: 0,
          top: '10%',
          left: '50%',
          fontWeight: 900,
          userSelect: 'none',
          whiteSpace: 'nowrap',
          position: 'absolute',
          color: 'transparent',
          fontFamily: 'Bebas Neue',
          transform: 'translateX(-50%)',
          fontSize: { xs: '6rem', md: '12rem' },
          WebkitTextStroke: '1px rgba(251, 191, 36, 0.05)',
        }}
      >
        REGISTER
      </Typography>

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Typography
            variant="overline"
            sx={{ color: '#fbbf24', fontWeight: 700, letterSpacing: '3px', mb: 1, display: 'block' }}
          >
            Join The Grid
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Bebas Neue',
              mb: 3,
              color: '#ffffff',
              fontSize: { xs: '3.5rem', md: '5rem' },
              letterSpacing: '2px',
            }}
          >
            Driver <Box component="span" sx={{ color: '#fbbf24' }}>Registration</Box>
          </Typography>
          <Box
            sx={{ width: '80px', height: '4px', bgcolor: '#fbbf24', margin: '0 auto 24px', borderRadius: '2px' }}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            bgcolor: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '50px 4px 50px 4px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              key="success-screen"
            >
              {/* Radial Top Glow */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-100px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '350px',
                  height: '350px',
                  background: 'radial-gradient(circle, rgba(251, 191, 36, 0.18) 0%, rgba(251, 191, 36, 0) 70%)',
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', py: { xs: 2, md: 3 } }}>
                {/* Animated Glowing Badge */}
                <motion.div
                  initial={{ scale: 0, rotate: -25 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 240, damping: 18, delay: 0.1 }}
                >
                  <Box
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: '50%',
                      margin: '0 auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(251, 191, 36, 0.12)',
                      border: '2px solid #fbbf24',
                      boxShadow: '0 0 40px rgba(251, 191, 36, 0.35)',
                      mb: 3,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 56, color: '#fbbf24' }} />
                  </Box>
                </motion.div>

                {/* Status Pill */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2.2,
                    py: 0.7,
                    borderRadius: '20px',
                    bgcolor: 'rgba(251, 191, 36, 0.12)',
                    border: '1px solid rgba(251, 191, 36, 0.35)',
                    color: '#fbbf24',
                    fontSize: '0.825rem',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    mb: 2.5,
                  }}
                >
                  <HourglassIcon sx={{ fontSize: 16 }} />
                  Status: Pending Review
                </Box>

                {/* Headline */}
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: 'Bebas Neue',
                    color: '#ffffff',
                    fontSize: { xs: '2.5rem', md: '3.6rem' },
                    letterSpacing: '2px',
                    mb: 1.5,
                  }}
                >
                  Registration Successfully <Box component="span" sx={{ color: '#fbbf24' }}>Submitted</Box>
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    maxWidth: 580,
                    mx: 'auto',
                    mb: 4.5,
                    lineHeight: 1.6,
                  }}
                >
                  Thank you, <strong style={{ color: '#ffffff' }}>{submittedData?.fullName}</strong>. Your driver registration profile has been recorded in the championship portal. The technical stewards will review your submission shortly.
                </Typography>

                {/* Driver Submission Summary Card */}
                <Box
                  sx={{
                    maxWidth: 620,
                    mx: 'auto',
                    p: { xs: 2.5, md: 3.5 },
                    borderRadius: '20px 4px 20px 4px',
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(251, 191, 36, 0.25)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    textAlign: 'left',
                    mb: 4.5,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, pb: 1.5, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                    <Typography sx={{ fontFamily: 'Bebas Neue', color: '#fbbf24', fontSize: '1.2rem', letterSpacing: '1px' }}>
                      Driver Submission Summary
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 600 }}>
                      REF: AAGC-{Date.now().toString().slice(-6)}
                    </Typography>
                  </Box>

                  {submittedData?.photoPreview && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, pb: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <Box
                        component="img"
                        src={submittedData.photoPreview}
                        alt="Driver Profile"
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '12px',
                          objectFit: 'cover',
                          border: '2px solid #fbbf24',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                        }}
                      />
                      <Box>
                        <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: '1.05rem' }}>
                          {submittedData?.fullName}
                        </Typography>
                        <Typography sx={{ color: '#10b981', fontSize: '0.825rem', fontWeight: 600 }}>
                          ✓ Official Driver Photo Attached
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Grid container spacing={2.5}>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Driver Full Name
                      </Typography>
                      <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                        {submittedData?.fullName || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Nationality
                      </Typography>
                      <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                        {submittedData?.nationality || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Email Address
                      </Typography>
                      <Typography sx={{ color: '#93c5fd', fontSize: '0.95rem' }}>
                        {submittedData?.email || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Contact Phone
                      </Typography>
                      <Typography sx={{ color: '#ffffff', fontSize: '0.95rem' }}>
                        {submittedData?.countryCode} {submittedData?.phone}
                      </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        ASN Country / Club
                      </Typography>
                      <Typography sx={{ color: '#ffffff', fontSize: '0.95rem' }}>
                        {submittedData?.asnName || 'None'} {submittedData?.asnCountry ? `(${submittedData?.asnCountry})` : ''}
                      </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        AAGC Championship Participation
                      </Typography>
                      <Typography sx={{ color: '#10b981', fontWeight: 700, fontSize: '0.95rem' }}>
                        {submittedData?.interestedInAAGC === 'yes' ? 'YES (Confirmed Interest)' : 'NO'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* What Happens Next? Process Timeline */}
                {/* <Box sx={{ maxWidth: 620, mx: 'auto', mb: 5, textAlign: 'left' }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', mb: 2, textAlign: 'center' }}>
                    What Happens Next?
                  </Typography>

                  <Grid container spacing={2}>
                    {[
                      { step: '01', title: 'Review & Verification', desc: 'Stewards check your eligibility, license & ASN details.' },
                      { step: '02', title: 'Official Email Notification', desc: 'You will receive status updates directly via your email.' },
                      { step: '03', title: 'Paddock Confirmation', desc: 'Approved competitors receive racing numbers & briefings.' },
                    ].map((item) => (
                      <Grid item size={{ xs: 12, md: 4 }} key={item.step}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: '10px',
                            bgcolor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            height: '100%',
                          }}
                        >
                          <Typography sx={{ fontFamily: 'Bebas Neue', color: '#fbbf24', fontSize: '1.2rem', mb: 0.5 }}>
                            {item.step}. {item.title}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.8rem', lineHeight: 1.5 }}>
                            {item.desc}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box> */}

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    onClick={handleRegisterAnother}
                    startIcon={<RestartIcon />}
                    sx={{
                      bgcolor: '#fbbf24',
                      color: '#000000',
                      px: 4.5,
                      py: 1.4,
                      borderRadius: '10px',
                      fontFamily: 'Bebas Neue',
                      fontSize: '1.2rem',
                      letterSpacing: '1px',
                      '&:hover': {
                        bgcolor: '#f59e0b',
                        boxShadow: '0 6px 20px rgba(251, 191, 36, 0.3)',
                      },
                    }}
                  >
                    Register Another Driver
                  </Button>
                </Box>
              </Box>
            </motion.div>
          ) : (
            <>
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  mb: 6,
                  '& .MuiStepLabel-label': { color: '#94a3b8', fontFamily: 'Bebas Neue', fontSize: '1rem' },
                  '& .MuiStepLabel-label.Mui-active': { color: '#fbbf24' },
                  '& .MuiStepLabel-label.Mui-completed': { color: '#fbbf24' },
                  '& .MuiStepIcon-root': { color: 'rgba(255, 255, 255, 0.1)' },
                  '& .MuiStepIcon-root.Mui-active': { color: '#fbbf24' },
                  '& .MuiStepIcon-root.Mui-completed': { color: '#fbbf24' },
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box component="div">
                <Box sx={{ minHeight: '300px' }}>
                  <AnimatePresence mode="wait">
                    {renderStepContent(activeStep)}
                  </AnimatePresence>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6 }}>
                  <Button
                    type="button"
                    disabled={activeStep === 0 || loading}
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      color: '#ffffff',
                      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.2)' },
                      fontFamily: 'Bebas Neue',
                      fontSize: '1.2rem',
                    }}
                  >
                    Back
                  </Button>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {activeStep === steps.length - 1 ? (
                      <Button
                        type="button"
                        variant="contained"
                        disabled={loading}
                        onClick={handleSubmit}
                        startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                        sx={{
                          bgcolor: '#fbbf24',
                          color: '#000000',
                          px: 5,
                          borderRadius: '10px',
                          fontFamily: 'Bebas Neue',
                          fontSize: '1.2rem',
                          '&:hover': { bgcolor: '#f59e0b' },
                        }}
                      >
                        {loading ? 'Submitting...' : 'Register'}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="contained"
                        onClick={handleNext}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          bgcolor: '#fbbf24',
                          color: '#000000',
                          px: 5,
                          borderRadius: '10px',
                          fontFamily: 'Bebas Neue',
                          fontSize: '1.2rem',
                          '&:hover': { bgcolor: '#f59e0b' },
                        }}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const inputStyles = {
  '& .MuiFilledInput-root': {
    bgcolor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      bgcolor: 'rgba(255, 255, 255, 0.08)',
      borderColor: 'rgba(251, 191, 36, 0.3)',
    },
    '&.Mui-focused': {
      bgcolor: 'rgba(255, 255, 255, 0.1)',
      borderColor: '#fbbf24',
      boxShadow: '0 0 0 2px rgba(251, 191, 36, 0.1)',
    },
    '& .input-icon': {
      color: '#94a3b8',
    },
    '&.Mui-focused .input-icon': {
      color: '#fbbf24',
    },
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      cursor: 'pointer',
      opacity: 0.6,
      transition: 'all 0.3s ease',
      filter: 'invert(69%) sepia(8%) saturate(763%) hue-rotate(182deg) brightness(97%) contrast(88%)', // Approx #94a3b8
    },
    '&.Mui-focused input[type="date"]::-webkit-calendar-picker-indicator': {
      opacity: 1,
      filter: 'invert(75%) sepia(61%) saturate(2335%) hue-rotate(350deg) brightness(101%) contrast(104%)', // Approx #fbbf24 (Amber)
    },
    borderRadius: '10px',
    color: '#ffffff',
    '&:before, &:after': {
      display: 'none',
    },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#fbbf24' },
};

const radioStyles = {
  color: 'rgba(255, 255, 255, 0.3)',
  '&.Mui-checked': { color: '#fbbf24' },
};

export default RegistrationForm;
